/**
 * @fileoverview Reopen dialog for a My Page target that is Awaiting Payment.
 * @description Opened from the target's details when the backend lists Reopen among the allowed
 * actions. Takes an optional reason and saves through Reopen Target with the version the details
 * showed as `If-Match`: the stage goes back to Under Negotiation and the sent payment details are
 * forgotten. A target that changed meanwhile is refused and shown for review. The parent mounts it
 * only while it is open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { useReopenMyPageTarget } from '@/features/sales/hooks/useReopenMyPageTarget'
import { isStaleMyPageError } from '@/features/sales/lib/my-page-stale.utils'
import { myPageReopenFormSchema } from '@/features/sales/schemas/my-page-reopen-form.schema'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import MyPageReasonField from './MyPageReasonField'
import SalesRequestError from './SalesRequestError'
import StaleVersionNotice from './StaleVersionNotice'

import type { MyPageReopenFormValues } from '@/features/sales/schemas/my-page-reopen-form.schema'
import type { MyPageTargetDetails } from '@/features/sales/types/my-page.types'
import type { FormEvent } from 'react'

interface MyPageReopenDialogProps {
  target: MyPageTargetDetails
  onClose: () => void
}

/**
 * @component
 * @param {MyPageReopenDialogProps} props - The target and the close handler.
 * @param {MyPageTargetDetails} props.target - The target as the details last showed it.
 * @param {() => void} props.onClose - Called when the dialog is dismissed or the target reopened.
 */
function MyPageReopenDialog({ target, onClose }: MyPageReopenDialogProps) {
  const { t } = useTranslation()
  const reopen = useReopenMyPageTarget(onClose)
  const refreshSales = useRefreshSalesQueries()
  const close = useGuardedClose(onClose, reopen.isPending)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MyPageReopenFormValues>({
    resolver: zodResolver(myPageReopenFormSchema),
    defaultValues: { reason: '' },
  })
  const isStale = reopen.isError && isStaleMyPageError(reopen.error)

  /**
   * @description Reopens the target against the version the details showed.
   * @param {MyPageReopenFormValues} values - The form's values.
   */
  const onValid = (values: MyPageReopenFormValues) => {
    reopen.mutate({
      targetId: target.targetId,
      version: target.version,
      input: values.reason ? { reason: values.reason } : {},
    })
  }

  /**
   * @description Reloads the latest state and returns to the details for review.
   */
  const onRefresh = () => {
    refreshSales()
    onClose()
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="my-page-reopen-dialog"
      title={t('sales.myWork.dialogs.reopen.title')}
      subtitle={target.bankName}
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4"
        data-testid="my-page-reopen-form"
      >
        <p className="text-[13.5px] text-ink">{t('sales.myWork.dialogs.reopen.description')}</p>
        <MyPageReasonField
          id="myPageReopenReason"
          data-testid="my-page-reopen-reason"
          label={t('sales.myWork.dialogs.reasonOptionalLabel')}
          placeholder={t('sales.myWork.dialogs.reasonPlaceholder')}
          error={errors.reason?.message ? t(errors.reason.message) : undefined}
          {...register('reason')}
        />
        {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={false} />}
        {reopen.isError && !isStale && <SalesRequestError error={reopen.error} />}
        <DialogActions
          testIdPrefix="my-page-reopen"
          cancelLabel={t('sales.myWork.dialogs.cancel')}
          confirmLabel={t('sales.myWork.dialogs.reopen.submit')}
          onCancel={close}
          confirmType="submit"
          isLoading={reopen.isPending}
          disabled={isStale}
        />
      </form>
    </Modal>
  )
}

export default MyPageReopenDialog
